    class ScrollProgressNav extends HTMLElement {
      constructor() {
        super();
        this.mainContainer = this.closest('.th-main-scroll-progress');
        this.progressBar = this.querySelector('.th-scroll-progress-bar');
        this.tabs = this.querySelectorAll('.th-scroll-nav-tab');
        this.select = this.querySelector('.th-scroll-nav-select');
        this.hideOnScroll = this.dataset.hideOnScroll === 'true';
        this.smoothScroll = this.dataset.smoothScroll === 'true';
        
        this.sections = new Map();
        this.observer = null;
        this.scrollTimeout = null;
        this.lastScrollY = 0;
        this.isScrollingToSection = false;
        this.isStuck = false;
        this.originalTop = 0;
        
        this.observerOptions = {
          root: null,
          rootMargin: '-100px 0px -60% 0px', // accounts for fixed header height
          threshold: [0.1, 0.25, 0.5]
        };

      }

      connectedCallback() {
        this.setupSections();
        this.setupIntersectionObserver();
        this.setupEventListeners();
        this.updateProgressBar();
        this.setupStickyBehavior();
        
        if (this.hideOnScroll) {
          this.setupScrollHiding();
        }
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
      }

      setupStickyBehavior() {
      this.originalTop = this.offsetTop + window.pageYOffset - 82; // adjust if needed

      const checkSticky = () => {
        const wasStuck = this.isStuck;
        this.isStuck = window.scrollY > this.originalTop;

        if (this.isStuck !== wasStuck) {
          this.mainContainer.classList.toggle('stuck', this.isStuck);
        }
      };

      window.addEventListener('scroll', checkSticky, { passive: true });
      checkSticky();
    }


      setupSections() {
        this.tabs.forEach(tab => {
          const targetSelector = tab.dataset.target;
          if (targetSelector) {
            this.findAndObserveSection(targetSelector);
          }
        });
      }

      findAndObserveSection(selector) {
        let element = document.querySelector(selector);
        
        if (element) {
          this.sections.set(selector, element);
          if (this.observer) {
            this.observer.observe(element);
          }
        } else {
          const checkForElement = () => {
            element = document.querySelector(selector);
            if (element) {
              this.sections.set(selector, element);
              if (this.observer) {
                this.observer.observe(element);
              }
            } else {
              setTimeout(checkForElement, 500);
            }
          };
          checkForElement();
        }
      }

      setupIntersectionObserver() {
        this.observer = new IntersectionObserver((entries) => {
          if (this.isScrollingToSection) return;
          
          let mostVisibleEntry = null;
          let maxRatio = 0;

          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
              maxRatio = entry.intersectionRatio;
              mostVisibleEntry = entry;
            }
          });

          if (mostVisibleEntry) {
            this.setActiveSection(mostVisibleEntry.target);
          }
        }, this.observerOptions);

        this.sections.forEach(section => {
          this.observer.observe(section);
        });
      }

      setActiveSection(activeElement) {
        const activeSelector = this.findSelectorForElement(activeElement);
        
        this.tabs.forEach(tab => {
          const isActive = tab.dataset.target === activeSelector;
          tab.classList.toggle('active', isActive);
          tab.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (this.select && activeSelector) {
          this.select.value = activeSelector;
        }
      }

      findSelectorForElement(element) {
        for (const [selector, sectionElement] of this.sections) {
          if (sectionElement === element) {
            return selector;
          }
        }
        return null;
      }

      setupEventListeners() {
        this.tabs.forEach(tab => {
          tab.addEventListener('click', (e) => {
            e.preventDefault();
            this.scrollToSection(tab.dataset.target);
          });
        });

        if (this.select) {
          this.select.addEventListener('change', (e) => {
            if (e.target.value) {
              this.scrollToSection(e.target.value);
            }
          });
        }

        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize);

        const mutationObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  this.sections.forEach((section, selector) => {
                    if (!document.contains(section)) {
                      this.sections.delete(selector);
                      if (this.observer) {
                        this.observer.unobserve(section);
                      }
                    }
                  });
                  
                  this.tabs.forEach(tab => {
                    const targetSelector = tab.dataset.target;
                    if (targetSelector && !this.sections.has(targetSelector)) {
                      this.findAndObserveSection(targetSelector);
                    }
                  });
                }
              });
            }
          });
        });

        mutationObserver.observe(document.body, {
          childList: true,
          subtree: true
        });
      }

      scrollToSection(selector) {
        const element = this.sections.get(selector) || document.querySelector(selector);
        
        if (element) {
          this.isScrollingToSection = true;
          
          const stickyHeight = this.offsetHeight;
          const offsetTop = element.offsetTop - stickyHeight - 20;
          
          if (this.smoothScroll) {
            window.scrollTo({
              top: Math.max(0, offsetTop),
              behavior: 'smooth'
            });
          } else {
            window.scrollTo(0, Math.max(0, offsetTop));
          }

                const onScrollEnd = () => {
            this.isScrollingToSection = false;
            window.removeEventListener('scroll', onScrollEnd);
          };
          window.addEventListener('scroll', onScrollEnd);

        }
      }

      handleScroll() {
        this.updateProgressBar();
        
        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
        }
        
        this.scrollTimeout = setTimeout(() => {
          this.isScrollingToSection = false;
        }, 150);
      }

      handleResize() {
        this.updateProgressBar();
        this.originalTop = this.getBoundingClientRect().top + window.pageYOffset;
      }

      updateProgressBar() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercent = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        
        this.progressBar.style.width = Math.min(100, Math.max(0, scrollPercent)) + '%';
      }

      setupScrollHiding() {
        let ticking = false;
        
        const updateScrollDirection = () => {
          const currentScrollY = window.pageYOffset;
          
          if (this.isStuck && currentScrollY > 100) {
            if (currentScrollY > this.lastScrollY && currentScrollY > this.offsetHeight) {
              this.classList.add('hidden');
            } else if (currentScrollY < this.lastScrollY) {
              this.classList.remove('hidden');
            }
          } else {
            this.classList.remove('hidden');
          }
          
          this.lastScrollY = currentScrollY;
          ticking = false;
        };

        const onScroll = () => {
          if (!ticking) {
            requestAnimationFrame(updateScrollDirection);
            ticking = true;
          }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
      }
    }

    customElements.define('scroll-progress-nav', ScrollProgressNav);